import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import {
    dateTimeField,
    mapField,
    offersField,
    textColumn,
    userChipField,
} from '../../../../../shared/components/kit/table/table.fields';
import {
    TableColumn,
    TableColumnOffersLayout,
    TableColumnType,
} from '../../../../../shared/components/kit/table/table.types';
import { UserChipRenderTypeEnum } from '../../../../../../components/kit/user-chip/user-chip.component.types';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input-v2/date-input.component';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { PlannedWorkWidgetStore } from './planned-work-widget.store';
import { plannedWorkFragment } from './planned-work-widget.query';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';
import { SocialCalendarWorkTypeEnum } from '@libs/types/src/services/enum/calendar-event-type.enum';
import { Operator } from '../../../../../../../../../data-grid/src/grid/grid-request.dto';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { UserChipComponent } from '../../../../../../components/kit/user-chip/user-chip.component';
import { MapLinkFormatEnum } from '../../../../../shared/components/map-link/map-link.types';
import { startOfDay } from '../../../../../../utils/date.utils';

@Component({
    selector: 'app-planned-work-widget',
    templateUrl: './planned-work-widget.component.html',
    styleUrl: './planned-work-widget.component.scss',
    providers: [PlannedWorkWidgetStore],
    imports: [
        TableComponent,
        FormField,
        DateInputComponent,
        ButtonComponent,
        PrimeTemplate,
        TranslatePipe,
        UserChipComponent,
    ],
})
export class PlannedWorkWidgetComponent {
    protected readonly store = inject(PlannedWorkWidgetStore);
    protected readonly prefix = 'dashboard.plannedWork.';

    protected readonly today = startOfDay();

    public readonly items = input<{ serviceId: string, widget: UserWidgetSetting }[]>([]);

    protected readonly dateFormData = signal({ date: this.today });
    protected readonly dateForm = form(this.dateFormData);
    protected readonly canGoToPreviousDay = computed(
        () => startOfDay(new Date(this.dateFormData().date)).getTime() > this.today.getTime(),
    );

    protected additionalFilters = computed(() => {
        const date = this.dateFormData().date;

        return new FilterBuilder({ serviceId: this.items().map(item => item.serviceId) })
            .date(date.toISOString(), 'dateFrom', Operator.EQ)
            .date(date.toISOString(), 'dateTo', Operator.EQ)
            .where('eventType', [
                SocialCalendarWorkTypeEnum.INDIVIDUAL_WORK,
                SocialCalendarWorkTypeEnum.GROUP_ACTIVITY,
            ], Operator.IN)
            .where('viewType', 'user')
            .where('hidden', 'false')
            .filter().filter;
    });

    protected readonly columns = computed<TableColumn[]>(() => {
        const showServiceInfo = [...new Set(this.items().map(x => x.serviceId))].length > 1;

        let cols = [
            {
                name: 'folders',
                dataKey: 'occurrenceFolders',
                filterable: false,
            },
            dateTimeField('dateFrom', false, false),
            userChipField('worker', UserChipRenderTypeEnum.User, {
                dataKey: 'occurrenceUsers',
                data: (workers: any[]) => workers?.[0],
                sortable: false,
                filterable: false,
            }),
            mapField('addressTo', MapLinkFormatEnum.SingleLine, false, 'addressTo', false, false),
            offersField('offers', TableColumnOffersLayout.Horizontal, {
                filterable: false,
            }),
            textColumn('note', false, false),
        ];

        if (showServiceInfo) {
            cols = [
                ...cols.slice(0, 1),
                {
                    name: 'service',
                    dataKey: 'occurrenceFolders',
                    data: (folders: any[]) => folders?.[0].serviceId,
                    type: TableColumnType.Service,
                },
                ...cols.slice(1),
            ];
        }

        return cols;
    });

    protected changeDay(days: number): void {
        const date = startOfDay(new Date(this.dateFormData().date));
        date.setDate(date.getDate() + days);
        if (date < this.today) return;

        this.dateFormData.set({ date });
    }

    protected readonly plannedWorkFragment = plannedWorkFragment;
    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
